import React, { useState, useEffect } from 'react';

const ZONE_PERCENTS = [
  { from: 0.50, to: 0.60 },
  { from: 0.60, to: 0.70 },
  { from: 0.70, to: 0.80 },
  { from: 0.80, to: 0.90 },
  { from: 0.90, to: 1.00 },
];

function calcZones(method, maxHR, hrRest) {
  if (!maxHR || maxHR <= 0) 
    return;

  const result = {};
  
  ZONE_PERCENTS.forEach((p, i) => {
    const key = `zone${i + 1}`;
    if (method === 'hrr') {
      const reserve = maxHR - (hrRest || 0);
      result[key] = {
        from: Math.round(p.from * reserve + (hrRest || 0)),
        to: Math.round(p.to * reserve + (hrRest || 0)),
      };
    } else {
      result[key] = {
        from: Math.round(p.from * maxHR),
        to: Math.round(p.to * maxHR),
      };
    }
  });
  return result;
}

const emptyZones = {
  zone1: { from: '', to: '' },
  zone2: { from: '', to: '' },
  zone3: { from: '', to: '' },
  zone4: { from: '', to: '' },
  zone5: { from: '', to: '' },
};

const isValidValue = (v) => v !== '' && Number(v) > 0;

// Props:
//   hasPremium  – boolean; if true the form is hidden and onChange is not called
//   onChange    – (zones) => void, called with { zone1: {from, to}, ..., zone5: {from, to} }
//   showErrors  – boolean; if true, invalid zone inputs are highlighted in red
function HeartRateZones({ hasPremium, onChange, showErrors }) {
  const [method, setMethod] = useState('maxhr'); // 'maxhr' | 'hrr'
  const [maxHR, setMaxHR] = useState('');
  const [hrRest, setHrRest] = useState('');
  const [zones, setZones] = useState(emptyZones);

  // Recalculate whenever inputs that drive the formula change
  useEffect(() => {
    const calculated = calcZones(method, Number(maxHR), Number(hrRest));
    if (calculated) {
      setZones(calculated);
      onChange && onChange(calculated);
    }
  }, [method, maxHR, hrRest]); // eslint-disable-line react-hooks/exhaustive-deps

   const handleZoneChange = (zoneKey, zoneValueFromOrTo, zoneNewFromOrTo) => {
    const value = zoneNewFromOrTo === '' ? '' : Number(zoneNewFromOrTo);
    const updated = {
      zone1: { ...zones.zone1 },
      zone2: { ...zones.zone2 },
      zone3: { ...zones.zone3 },
      zone4: { ...zones.zone4 },
      zone5: { ...zones.zone5 },
    };
    updated[zoneKey][zoneValueFromOrTo] = value;
    setZones(updated);
    onChange && onChange(updated);
  };

  const zonesComplete = [1, 2, 3, 4, 5].every(n => {
    const z = zones[`zone${n}`];
    return isValidValue(z.from) && isValidValue(z.to);
  });

  const zonesValidated = [2, 3, 4, 5].every(n => {
    const previous_zone = zones[`zone${n-1}`]
    const current_zone = zones[`zone${n}`]
    return previous_zone['to'] <= current_zone['from']
  })

  if (hasPremium) {
    return;
  }

  return (
    <div className="field-wrapper">
      <label className="field-label">Heart Rate Zones</label>

      {/* Method */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        <label style={{ fontSize: 13, color: '#444', cursor: 'pointer' }}>
          <input
            type="radio"
            value="maxhr"
            checked={method === 'maxhr'}
            onChange={() => setMethod('maxhr')}
          />{' '}
          % of Max Heart Rate
        </label>
        <label style={{ fontSize: 13, color: '#444', cursor: 'pointer' }}>
          <input
            type="radio"
            value="hrr"
            checked={method === 'hrr'}
            onChange={() => setMethod('hrr')}
          />{' '}
          % of Heart Rate Reserve
        </label>
      </div>

      {/* Max HR */}
      <div className="field-wrapper">
        <label className="field-label" htmlFor="maxHR">Maximum Heart Rate</label>
        <input
          id="maxHR"
          className="input no-spinner"
          type="number"
          min="1"
          value={maxHR}
          onChange={(e) => setMaxHR(e.target.value)}
          placeholder="e.g. 190"
          style={{ maxWidth: 140 }}
        />
      </div>

      {/* Resting HR (HRR only) */}
      {method === 'hrr' && (
        <div className="field-wrapper">
          <label className="field-label" htmlFor="hrRest">Resting Heart Rate</label>
          <input
            id="hrRest"
            className="input no-spinner"
            type="number"
            min="1"
            value={hrRest}
            onChange={(e) => setHrRest(e.target.value)}
            placeholder="e.g. 60"
            style={{ maxWidth: 140 }}
          />
        </div>
      )}

      {/* Zone rows */}
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
          <span style={{ width: 52 }} />
          <span className="zone-input" style={{ fontSize: 12, color: '#888', textAlign: 'center' }}>From</span>
          <span style={{ width: 14 }} />
          <span className="zone-input" style={{ fontSize: 12, color: '#888', textAlign: 'center' }}>To</span>
        </div>
        {[1, 2, 3, 4, 5].map((n) => {
          const zoneKey = `zone${n}`;
          return (
            <div key={zoneKey} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ width: 52, fontSize: 13, fontWeight: 600, color: '#444' }}>Zone {n}</span>
              <input
                className={`input zone-input`}
                type="number"
                min="1"
                value={zones[zoneKey].from}
                onChange={(e) => handleZoneChange(zoneKey, 'from', e.target.value)}
              />
              <span style={{ color: '#888' }}>–</span>
              <input
                className={`input zone-input`}
                type="number"
                min="1"
                value={zones[zoneKey].to}
                onChange={(e) => handleZoneChange(zoneKey, 'to', e.target.value)}
              />
            </div>
          );
        })}
      </div>
      {showErrors &&!zonesComplete && (
        <p className="field-error">Please fill in all heart rate zones before continuing.</p>
      )}
      {!zonesValidated && (
        <p className="field-error">Please make sure that the upper limit of a zone, is lower or equal to the lower limit of the next zone.</p>
      )}
    </div>
  );
}

export default HeartRateZones;
